import { useEffect, useState } from 'react';

import type {
  InteractiveQuestionBankInteractiveQuestion,
  InteractiveQuestionBankResponse,
} from '@genially/contracts';
import type { InteractiveQuestionState } from '@genially/p2p-lib';

import type { PlayPostMessageInteractiveQuestion } from '../../../../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';
import { ViewApiService } from '../../../../../../../../../shared/infrastructure/ViewApiService';

/**
 * One request per bank for the whole page: a bank cannot change while it is being played,
 * and the panel re-renders on every arriving answer — without this, opening a random
 * question would ask the view for the same bank on every event.
 */
const requestsByBankId = new Map<
  string,
  Promise<InteractiveQuestionBankResponse | null>
>();

const getBank = (bankId: string) => {
  const cached = requestsByBankId.get(bankId);

  if (cached) {
    return cached;
  }

  const request = ViewApiService.getInteractiveQuestionBank(bankId).then(bank => {
    // A failed fetch is not cached as a failure: `getInteractiveQuestionBank` swallows the
    // error and returns null, and the next question opened should try again.
    if (bank === null) {
      requestsByBankId.delete(bankId);
    }

    return bank;
  });

  requestsByBankId.set(bankId, request);

  return request;
};

/**
 * Which bank question the host drew for this element. The draw is broadcast for the whole
 * session, so it is the same for everyone; when that broadcast has not arrived, any
 * player's own answer carries the id it was answered against.
 */
const bankQuestionIdOf = (
  questionId: string,
  assignments: Record<string, string> | undefined,
  state: InteractiveQuestionState | undefined,
) =>
  assignments?.[questionId] ??
  state?.playerUserAnswers
    .map(
      ({ answer }) =>
        (answer as { bankInteractiveQuestionId?: string }).bankInteractiveQuestionId,
    )
    .find(Boolean);

/**
 * The bank question a random element drew, so the panel can name the options a
 * participant picked. Undefined for every other kind of question, and while the bank is
 * on its way — the rows then simply show no detail, as they did before.
 */
export const useRandomBankQuestion = ({
  question,
  state,
  assignments,
}: {
  question: PlayPostMessageInteractiveQuestion | undefined;
  state: InteractiveQuestionState | undefined;
  /** `game.randomQuestionAssignments`: elementId -> the bank question drawn for it. */
  assignments: Record<string, string> | undefined;
}): InteractiveQuestionBankInteractiveQuestion | undefined => {
  const [bankQuestion, setBankQuestion] =
    useState<InteractiveQuestionBankInteractiveQuestion>();

  const bankId =
    question && 'BankId' in question
      ? (question.BankId as string | undefined)
      : undefined;
  const bankQuestionId = question
    ? bankQuestionIdOf(question.Id, assignments, state)
    : undefined;

  useEffect(() => {
    if (!bankId || !bankQuestionId) {
      setBankQuestion(undefined);

      return undefined;
    }

    let isCurrent = true;

    getBank(bankId).then(bank => {
      if (isCurrent) {
        setBankQuestion(
          bank?.interactiveQuestions.find(({ id }) => id === bankQuestionId),
        );
      }
    });

    // The host can move to another question while the bank is in flight, and the answer
    // would then land on a question it does not belong to.
    return () => {
      isCurrent = false;
    };
  }, [bankId, bankQuestionId]);

  return bankQuestion;
};
